import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Plus, Pencil, Trash2, FileText, Code, Eye, Shield, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TemplatesPage = () => {
  const { canManageSendersAndTemplates, isAdmin } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    html_content: '',
    description: '',
    is_private: false
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      subject: '',
      html_content: getDefaultTemplate(),
      description: '',
      is_private: false
    });
    setDialogOpen(true);
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      html_content: template.html_content,
      description: template.description || '',
      is_private: template.is_private || false
    });
    setDialogOpen(true);
  };

  const openPreview = (template) => {
    setPreviewTemplate(template);
    setPreviewOpen(true);
  };

  const getDefaultTemplate = () => {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Email Template</title>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">
    <tr>
      <td style="padding: 40px 30px;">
        <h1 style="color: #333333; margin-bottom: 20px;">Hello, {{first_name}}!</h1>
        <p style="color: #666666; line-height: 1.6;">
          Welcome to Unified Lending Hub. We're excited to have you on board.
        </p>
        <p style="color: #666666; line-height: 1.6;">
          If you have any questions, feel free to reach out to our support team.
        </p>
        <p style="color: #666666; line-height: 1.6; margin-top: 30px;">
          Best regards,<br>
          The Unified Lending Hub Team
        </p>
      </td>
    </tr>
  </table>
</body>
</html>`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingTemplate) {
        await axios.put(`${API_URL}/templates/${editingTemplate.id}`, formData);
        toast.success('Template updated successfully');
      } else {
        await axios.post(`${API_URL}/templates`, formData);
        toast.success('Template created successfully');
      }
      setDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await axios.delete(`${API_URL}/templates/${templateId}`);
      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const getPreviewHtml = (html) => {
    return html.replace(/\{\{first_name\}\}/g, 'John');
  };

  if (!canManageSendersAndTemplates()) {
    return (
      <div className="text-center py-12">
        <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-900">Access Denied</h2>
        <p className="text-slate-500">You don't have permission to manage templates.</p>
      </div>
    );
  }

  return (
    <div data-testid="templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Email Templates
          </h1>
          <p className="text-slate-500 mt-1">Create and manage HTML email templates</p>
        </div>
        <Button
          onClick={openCreateDialog}
          className="mt-4 sm:mt-0"
          data-testid="add-template-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Template
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-violet-200 bg-violet-50 mb-6">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Code className="w-5 h-5 text-violet-600 mt-0.5" />
            <div>
              <p className="font-medium text-violet-900">Dynamic Variables</p>
              <p className="text-sm text-violet-700">
                Use <code className="bg-violet-100 px-1.5 py-0.5 rounded text-violet-800">{'{{first_name}}'}</code> in your templates. 
                It will be automatically replaced with the customer's first name when sending emails.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Templates Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-lg" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
              ))}
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900">No templates yet</h3>
              <p className="text-slate-500 mb-4">Create your first email template to get started.</p>
              <Button onClick={openCreateDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Template</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id} data-testid={`template-row-${template.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${template.is_private ? 'bg-amber-100' : 'bg-violet-100'}`}>
                          {template.is_private ? (
                            <Lock className="w-5 h-5 text-amber-600" />
                          ) : (
                            <FileText className="w-5 h-5 text-violet-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900">{template.name}</p>
                            {template.is_private && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                Private
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">{template.description || 'No description'}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600 max-w-xs truncate">
                      {template.subject}
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {new Date(template.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openPreview(template)}
                          data-testid={`preview-template-${template.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(template)}
                          data-testid={`edit-template-${template.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(template.id)}
                          data-testid={`delete-template-${template.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingTemplate ? 'Edit Template' : 'Create New Template'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Template Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Welcome Email"
                  required
                  data-testid="template-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject">Email Subject</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Welcome, {{first_name}}!"
                  required
                  data-testid="template-subject-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Used for new customer onboarding"
                data-testid="template-description-input"
              />
            </div>
            
            {/* Private Template Option - Admin Only */}
            {isAdmin() && (
              <div className="flex items-center justify-between p-4 rounded-lg border border-amber-200 bg-amber-50">
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-900">Private Template</p>
                    <p className="text-sm text-amber-700">Only you (admin) can see and use this template</p>
                  </div>
                </div>
                <Switch
                  checked={formData.is_private}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_private: checked })}
                  data-testid="template-private-toggle"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="html_content">HTML Content</Label>
              <Textarea
                id="html_content"
                value={formData.html_content}
                onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                placeholder="Enter your HTML template..."
                rows={15}
                className="font-mono text-sm"
                required
                data-testid="template-html-input"
              />
              <p className="text-xs text-slate-500">
                Use {'{{first_name}}'} to insert the customer's first name dynamically.
              </p>
            </div>
            
            {/* Live Preview */}
            <div className="space-y-2">
              <Label>Live Preview</Label>
              <div className="border rounded-lg p-4 bg-slate-50 max-h-64 overflow-y-auto">
                <iframe
                  srcDoc={getPreviewHtml(formData.html_content)}
                  title="Template Preview"
                  className="w-full h-48 border-0"
                  sandbox="allow-same-origin"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-template-btn">
                {submitting ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Preview: {previewTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-100 p-3 rounded-lg">
              <p className="text-sm text-slate-600">
                <span className="font-medium">Subject:</span> {previewTemplate?.subject.replace(/\{\{first_name\}\}/g, 'John')}
              </p>
            </div>
            <div className="border rounded-lg bg-white">
              <iframe
                srcDoc={previewTemplate ? getPreviewHtml(previewTemplate.html_content) : ''}
                title="Template Preview"
                className="w-full h-96 border-0"
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplatesPage;
